import { useEffect } from "react";
import { isExpectedDomRemovalError } from "@/lib/errors";

export interface ChatwootWidgetProps {
  websiteToken?: string;
  baseUrl?: string;
  className?: string;
  /**
   * If true, silence the known 404s from Chatwoot's typing indicator endpoint
   * before a conversation exists by short‑circuiting that request to 204.
   */
  suppressTyping404?: boolean;
}

// Lightweight wrapper to load and initialize Chatwoot's Web SDK dynamically.
// Docs: https://www.chatwoot.com/docs/product/channels/live-chat/sdk/
export function ChatwootWidget({
  websiteToken = "iyPQuwbzfW86BD1LJL9adwnT",
  baseUrl = "https://chat.responda-ai.com",
  suppressTyping404 = true,
}: ChatwootWidgetProps) {
  useEffect(() => {
    if (typeof window === "undefined") return; // SSR safety

  const DEV = (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) || process.env.NODE_ENV !== 'production';
    const log = (...args: any[]) => { if (DEV) console.info(...args); };
    const warn = (...args: any[]) => { if (DEV) console.warn(...args); };

    const w = window as Window & {
      chatwootSDK?: ChatwootSDK;
      __chatwootInitialized?: boolean;
      __chatwootMounts?: number;
      __chatwootFetchPatched?: boolean;
      __chatwootOriginalFetch?: typeof fetch;
    };
    log('[Chatwoot] mounting widget');
    const scriptSrc = `${baseUrl}/packs/js/sdk.js`;

    // Optionally intercept the widget typing endpoint (which returns 404 until
    // the first message creates a conversation). This prevents noisy console errors
    // without affecting other network calls.
    // Track how many ChatwootWidget instances are mounted globally
    const previousMounts = w.__chatwootMounts ?? 0;
    w.__chatwootMounts = previousMounts + 1;
    log('[Chatwoot] mounts incremented:', w.__chatwootMounts);

    if (
      previousMounts === 0 &&
      suppressTyping404 &&
      typeof w.fetch === 'function' &&
      !w.__chatwootFetchPatched
    ) {
      try {
        const originalFetch = w.fetch.bind(w);
        const typingPath = "/api/v1/widget/conversations/toggle_typing";
        const base = baseUrl?.replace(/\/$/, "");
        const fullPrefix = base ? `${base}${typingPath}` : typingPath;
        w.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
          try {
            const url = typeof input === 'string' ? input : (input as Request).url ?? String(input);
            if (url.includes(typingPath) && (!base || url.startsWith(fullPrefix))) {
              if (DEV) console.debug('[Chatwoot] silencing toggle_typing 404 with 204 (no conversation yet)');
              return new Response(null, { status: 204, statusText: 'Silenced toggle_typing' });
            }
          } catch {
            // fall through to original fetch
          }
          return originalFetch(input as any, init);
        }) as typeof fetch;
        w.__chatwootFetchPatched = true;
        w.__chatwootOriginalFetch = originalFetch;
      } catch {
        if (DEV) console.debug('[Chatwoot] failed to patch fetch');
      }
    }

    // If SDK is already present, just (re)run it once.
    const run = () => {
      try {
        if (w.chatwootSDK && !w.__chatwootInitialized) {
          log('[Chatwoot] calling run with', { websiteToken, baseUrl });
          w.chatwootSDK.run({ websiteToken, baseUrl });
          w.__chatwootInitialized = true;
          log('[Chatwoot] initialized');
        }
      } catch {
        // no-op
      }
    };
    const onLoad = () => { log('[Chatwoot] sdk load event'); run(); };
    const onError = () => warn('[Chatwoot] Failed to load SDK:', scriptSrc);


    let addedByUs = false;
    let scriptEl = document.querySelector(
      `script[src="${scriptSrc}"]`
    ) as HTMLScriptElement | null;

    if (w.chatwootSDK) {
      log('[Chatwoot] SDK already present; running now');
      run();
    } else {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.src = scriptSrc;
        scriptEl.async = true;
        scriptEl.defer = true;
        scriptEl.dataset.chatwoot = "true";
        scriptEl.addEventListener("load", onLoad, { once: true });
        scriptEl.addEventListener("error", onError, { once: true });
        (document.head || document.body).appendChild(scriptEl);
        addedByUs = true;
      } else {
        log('[Chatwoot] sdk script element already present; attaching listeners');
        // If a script tag exists but SDK not yet available, attach listeners.
        scriptEl.addEventListener("load", onLoad, { once: true });
        scriptEl.addEventListener("error", onError, { once: true });
      }
    }

    // Cleanup on unmount: manage global mounts and remove listeners; only tear down when last instance unmounts
    return () => {
      // Defensive cleanup: verify existence before manipulating DOM to avoid conflicts with React
      try {
        if (scriptEl) {
          try {
            scriptEl.removeEventListener("load", onLoad);
            scriptEl.removeEventListener("error", onError);
          } catch (error) {
            if (DEV && !isExpectedDomRemovalError(error)) {
              console.debug('[Chatwoot] Error removing listeners:', error);
            }
          }
        }
        w.__chatwootMounts = Math.max(0, (w.__chatwootMounts ?? 0) - 1);
        log('[Chatwoot] mounts decremented:', w.__chatwootMounts);
        if ((w.__chatwootMounts ?? 0) === 0) {
          log('[Chatwoot] last instance unmounted; cleaning up SDK script and flags');
          // Do not remove the widget container; let the SDK manage its DOM.
          if (addedByUs && scriptEl) {
            try {
              const parent = scriptEl.parentNode;
              if (parent && document.contains(scriptEl) && parent.contains(scriptEl)) {
                parent.removeChild(scriptEl);
              }
            } catch (error) {
              if (!isExpectedDomRemovalError(error)) {
                if (DEV) {
                  console.warn('[Chatwoot] Unexpected error removing script:', error);
                }
              }
            }
          }
          if (w.__chatwootFetchPatched && w.__chatwootOriginalFetch) {
            try {
              w.fetch = w.__chatwootOriginalFetch;
            } catch {
              // ignore restore errors
            }
            w.__chatwootFetchPatched = false;
            w.__chatwootOriginalFetch = undefined;
          }
          w.__chatwootInitialized = false;
        }
      } catch {
        // no-op
      }
    };
  }, [websiteToken, baseUrl, suppressTyping404]);

  // The SDK injects its own floating button and widget into the DOM
  return null;
}
